// Setup → Partners: the staff routes of the partner portal (apps/server/src/partners.mjs).
// The reads are MOTHER-110's; the writes, MOTHER-68's, are called from the page by MOTHER-118.
//
//   GET  /api/partners/users?status=&q=&referred=&sort=&limit=&offset=  { partnerUsers, total, counts, limit, offset }
//        (referred=yes|no and sort=referrals are MOTHER-133's; the page sends them from MOTHER-135)
//   GET  /api/partners/users/:id                        { partnerUser }, or 404 { error: 'No such partner.' }
//   POST /api/partners/users/:id/verify                 { partnerUser }   (no body: the server matches or creates the account)
//   POST /api/partners/users/:id/decline   { note? }    { partnerUser }
//   POST /api/partners/users/:id/deactivate             { partnerUser }
//   POST /api/partners/invite { email, firstName, lastName, accountId }   201 { partnerUser }
//   GET  /api/partners/users/:id/referrals?stage=&limit=&offset=   (MOTHER-122; read by MOTHER-123, below)
//   PATCH /api/partners/users/:id { olyticContactEmail }  { partnerUser }   (MOTHER-136; called by MOTHER-137, below)
//   GET  /api/list-views/people?q=                       { people: [{ id, name }] }   (the Olytic Contact lookup; `id` is the email)
//   PATCH /api/partners/users/:id { referralRate }  and  GET /api/partners/users/:id/referral-rate-history   (MOTHER-143; called by MOTHER-144, below)
//
// A refusal answers { error }: 409 when the status does not allow it, 400 for bad input,
// 404 'No such partner.'. The calls throw a PartnersApiError carrying that text.

import type { FilterOption } from '@olytic/foundation';

import { objectListUrl, parseObjectListResponse } from '../objects/objectsApi';

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

export const PARTNERS_API = '/api/partners/users';

/** At most this many rows a page (the route allows up to 100). */
export const PARTNERS_PAGE_SIZE = 50;

/** partner_user.status (migration 0069), in the order the page's tabs list them. */
export const PARTNER_STATUSES = ['pending', 'verified', 'declined', 'deactivated'] as const;
export type PartnerStatus = (typeof PARTNER_STATUSES)[number];

/** The list's `referred` filter (MOTHER-133): partners with a referral in any stage, or with none. */
export const PARTNER_REFERRED = ['yes', 'no'] as const;
export type PartnerReferred = (typeof PARTNER_REFERRED)[number];

/** The list's one other order: most referrals first. Newest application first is the route's default, sent as nothing. */
export const PARTNER_SORT_REFERRALS = 'referrals';
export type PartnerSort = typeof PARTNER_SORT_REFERRALS;

/** What the page says for a partner id the detail route does not know (its 404). */
export const NO_SUCH_PARTNER = 'No such partner.';

/** One row of the list: what `staffPartnerView` sends. */
export interface PartnerUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  companyName: string;
  status: string;
  accountId: string | null;
  contactId: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
}

/** One row of the list (`staffPartnerListView`): the partner, plus their referrals in summary (MOTHER-129). */
export interface PartnerListRow extends PartnerUser {
  /** Every referral they have made, in any stage; 0 for none. */
  referralCount: number;
  /** When their newest referral was made; null for none. */
  lastReferralAt: string | null;
}

/** The person at Olytic a partner deals with (MOTHER-136): shown on the partner's home page. */
export interface OlyticContact {
  name: string;
  email: string;
}

/** One partner on the detail route: the row, plus what they typed when they applied (`staffPartnerDetail`). */
export interface PartnerDetail extends PartnerUser {
  companyWebsite: string | null;
  phone: string | null;
  heardAbout: string | null;
  partnershipVision: string | null;
  /** Percent, e.g. 10. */
  referralRate: number | null;
  lastSignedInAt: string | null;
  /** Null when nobody is assigned. */
  olyticContact: OlyticContact | null;
}

export type PartnerCounts = Record<PartnerStatus, number>;

export interface PartnerList {
  partnerUsers: PartnerListRow[];
  /** Rows the filters match, across every page. */
  total: number;
  /** Every partner by status, whatever the filters: the tabs' counts. */
  counts: PartnerCounts;
  limit: number;
  offset: number;
}

export interface PartnerListQuery {
  /** A status, or empty for all of them. */
  status?: PartnerStatus | '' | undefined;
  q?: string | undefined;
  /** Only partners who have (`yes`) or have not (`no`) referred; absent or null for every partner. */
  referred?: PartnerReferred | null | undefined;
  /** `referrals` for most referrals first; absent or null for newest application first. */
  sort?: PartnerSort | null | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

/** The detail route's answer: the partner, or the page's "No such partner." for a 404. */
export type PartnerLookup = { found: true; partner: PartnerDetail } | { found: false; message: string };

export class PartnersApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;
const str = (value: unknown): string => (typeof value === 'string' ? value : '');
const strOrNull = (value: unknown): string | null => (typeof value === 'string' && value !== '' ? value : null);
const count = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0);

/** The list route's URL: `status`, `q`, `referred` and `sort` only when given, `limit` and `offset` always. */
export function partnersListUrl({ status, q, referred, sort, limit = PARTNERS_PAGE_SIZE, offset = 0 }: PartnerListQuery = {}): string {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  const search = q?.trim() ?? '';
  if (search !== '') params.set('q', search);
  if (referred) params.set('referred', referred);
  if (sort) params.set('sort', sort);
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  return `${PARTNERS_API}?${params.toString()}`;
}

export const partnerUrl = (id: string): string => `${PARTNERS_API}/${encodeURIComponent(id)}`;

function parsePartnerUser(value: unknown): PartnerUser {
  if (!isObject(value) || typeof value.id !== 'string') throw new PartnersApiError('The server sent a partner the page cannot read.', 500);
  return {
    id: value.id,
    email: str(value.email),
    firstName: str(value.firstName),
    lastName: str(value.lastName),
    companyName: str(value.companyName),
    status: str(value.status),
    accountId: strOrNull(value.accountId),
    contactId: strOrNull(value.contactId),
    decidedAt: strOrNull(value.decidedAt),
    decisionNote: strOrNull(value.decisionNote),
    createdAt: str(value.createdAt),
  };
}

function parsePartnerListRow(value: unknown): PartnerListRow {
  const row = parsePartnerUser(value);
  const listed = value as Record<string, unknown>;
  return {
    ...row,
    referralCount: typeof listed.referralCount === 'number' && Number.isInteger(listed.referralCount) && listed.referralCount >= 0 ? listed.referralCount : 0,
    lastReferralAt: strOrNull(listed.lastReferralAt),
  };
}

function parsePartnerDetail(value: unknown): PartnerDetail {
  const row = parsePartnerUser(value);
  const detail = value as Record<string, unknown>;
  return {
    ...row,
    companyWebsite: strOrNull(detail.companyWebsite),
    phone: strOrNull(detail.phone),
    heardAbout: strOrNull(detail.heardAbout),
    partnershipVision: strOrNull(detail.partnershipVision),
    referralRate: typeof detail.referralRate === 'number' && Number.isFinite(detail.referralRate) ? detail.referralRate : null,
    lastSignedInAt: strOrNull(detail.lastSignedInAt),
    olyticContact: parseOlyticContact(detail.olyticContact),
  };
}

/** `{ name, email }`, or null for no contact (or one without an email); a missing name reads as the email. */
function parseOlyticContact(value: unknown): OlyticContact | null {
  if (!isObject(value) || typeof value.email !== 'string' || value.email === '') return null;
  const name = typeof value.name === 'string' && value.name.trim() !== '' ? value.name.trim() : value.email;
  return { name, email: value.email };
}

/** The JSON body of an answer; a failure throws the server's `error` when it sent one, else `fallback`. */
async function read(response: Response, fallback: string): Promise<Record<string, unknown>> {
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) throw new PartnersApiError(isObject(body) && typeof body.error === 'string' && body.error !== '' ? body.error : fallback, response.status);
  if (!isObject(body)) throw new PartnersApiError(fallback, response.status);
  return body;
}

export async function listPartners(fetcher: Fetch, query: PartnerListQuery = {}): Promise<PartnerList> {
  const body = await read(await fetcher(partnersListUrl(query)), 'Could not load partners.');
  if (!Array.isArray(body.partnerUsers)) throw new PartnersApiError('The server sent a list the page cannot read.', 500);
  const counts = isObject(body.counts) ? body.counts : {};
  return {
    partnerUsers: body.partnerUsers.map(parsePartnerListRow),
    total: count(body.total),
    counts: Object.fromEntries(PARTNER_STATUSES.map((s) => [s, count(counts[s])])) as PartnerCounts,
    limit: count(body.limit) || PARTNERS_PAGE_SIZE,
    offset: count(body.offset),
  };
}

export async function getPartner(fetcher: Fetch, id: string): Promise<PartnerLookup> {
  const response = await fetcher(partnerUrl(id));
  if (response.status === 404) {
    await response.body?.cancel().catch(() => undefined);
    return { found: false, message: NO_SUCH_PARTNER };
  }
  const body = await read(response, 'Could not load this partner.');
  return { found: true, partner: parsePartnerDetail(body.partnerUser) };
}

/* ── The staff writes (MOTHER-118) ─────────────────────────────── */

/** The longest decline note the route takes (partners.mjs TEXT_MAX). */
export const NOTE_MAX = 2000;
/** The route's message for a longer note, word for word. */
export const NOTE_TOO_LONG = `Note must be ${NOTE_MAX.toLocaleString('en-US')} characters or fewer.`;

export const PARTNER_INVITE_API = '/api/partners/invite';

export type PartnerAction = 'verify' | 'decline' | 'deactivate';

export const partnerActionUrl = (id: string, action: PartnerAction): string => `${partnerUrl(id)}/${action}`;

/** What every write answers: the partner as the list shows them. */
export interface PartnerWrite {
  partnerUser: PartnerUser;
}

const JSON_HEADERS = { 'content-type': 'application/json' };

async function write(fetcher: Fetch, url: string, body: Record<string, string> | null, fallback: string): Promise<PartnerWrite> {
  const init: RequestInit = body === null ? { method: 'POST' } : { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) };
  const answer = await read(await fetcher(url, init), fallback);
  return { partnerUser: parsePartnerUser(answer.partnerUser) };
}

/** Verify: no body, so the server matches an account and contact from the application, or creates them. */
export const verifyPartner = (fetcher: Fetch, id: string): Promise<PartnerWrite> =>
  write(fetcher, partnerActionUrl(id, 'verify'), null, 'Could not verify this partner.');

/** Decline, with `{ note }` only when a note is given (trimmed); otherwise no body. */
export function declinePartner(fetcher: Fetch, id: string, note?: string | null): Promise<PartnerWrite> {
  const text = note?.trim() ?? '';
  return write(fetcher, partnerActionUrl(id, 'decline'), text === '' ? null : { note: text }, 'Could not decline this partner.');
}

export const deactivatePartner = (fetcher: Fetch, id: string): Promise<PartnerWrite> =>
  write(fetcher, partnerActionUrl(id, 'deactivate'), null, 'Could not deactivate this partner.');

/** What the Invite Partner form holds: each field as typed ('' when left empty). */
export interface PartnerInvite {
  email: string;
  firstName: string;
  lastName: string;
  accountId: string;
}

export const INVITE_FIELDS: readonly { key: keyof PartnerInvite; label: string }[] = [
  { key: 'email', label: 'Email' },
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'accountId', label: 'Account' },
];

// The API's email shape (partners.mjs EMAIL), the same check registration and referrals make.
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The checks the invite route makes that the form can make first, with the route's
 * messages: every field required (blank after trimming counts as empty), and a plausible
 * email. An empty object means the invite may be sent.
 */
export function validateInvite(values: PartnerInvite): Partial<Record<keyof PartnerInvite, string>> {
  const errors: Partial<Record<keyof PartnerInvite, string>> = {};
  for (const { key, label } of INVITE_FIELDS) if (values[key].trim() === '') errors[key] = `${label} is required.`;
  if (!errors.email && !EMAIL.test(values.email.trim())) errors.email = 'Email must be an email address.';
  return errors;
}

/** Invite: the four fields, trimmed. Answers 201 with the new, already verified, partner. */
export function invitePartner(fetcher: Fetch, values: PartnerInvite): Promise<PartnerWrite> {
  const body = Object.fromEntries(INVITE_FIELDS.map(({ key }) => [key, values[key].trim()]));
  return write(fetcher, PARTNER_INVITE_API, body, 'Could not invite this partner.');
}

/**
 * The Invite form's Account lookup: up to 20 accounts matching what was typed, through the
 * accounts list route every other account lookup in the app uses (lookupOptions.ts), but
 * with the page's own fetcher.
 */
export async function searchAccounts(fetcher: Fetch, query: string): Promise<FilterOption[]> {
  const page = await parseObjectListResponse(await fetcher(objectListUrl('accounts', { q: query, limit: 20, offset: 0 })));
  return page.rows.map((row) => ({ value: row.id, label: typeof row.name === 'string' && row.name.trim() !== '' ? row.name.trim() : row.id }));
}

/* ── A partner's Olytic contact (MOTHER-137) ───────────────────── */

export const PEOPLE_API = '/api/list-views/people';

/**
 * The Olytic Contact lookup: the people at Olytic the server offers (the same people the list
 * views share with), by name, each one's email as its value. A refusal (a 403) throws the
 * server's text, and a network failure a plain one, so the lookup shows it in its list.
 */
export async function searchPeople(fetcher: Fetch, query: string): Promise<FilterOption[]> {
  const response = await fetcher(`${PEOPLE_API}?${new URLSearchParams({ q: query }).toString()}`).catch(() => {
    throw new PartnersApiError('Could not load people.', 0);
  });
  const body = await read(response, 'Could not load people.');
  if (!Array.isArray(body.people)) throw new PartnersApiError('The server sent people the page cannot read.', 500);
  return body.people.flatMap((person: unknown) =>
    isObject(person) && typeof person.id === 'string' && person.id !== ''
      ? [{ value: person.id, label: typeof person.name === 'string' && person.name.trim() !== '' ? person.name.trim() : person.id }]
      : [],
  );
}

/** What the change answers: the partner as the list shows them, and their contact now. */
export interface OlyticContactWrite {
  partnerUser: PartnerUser;
  olyticContact: OlyticContact | null;
}

/** Sets the partner's Olytic contact to the person with this email, or clears it for null. */
export async function setOlyticContact(fetcher: Fetch, id: string, email: string | null): Promise<OlyticContactWrite> {
  const init: RequestInit = { method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify({ olyticContactEmail: email }) };
  const answer = await read(await fetcher(partnerUrl(id), init), 'Could not change the Olytic contact.');
  const partnerUser = parsePartnerUser(answer.partnerUser);
  return { partnerUser, olyticContact: parseOlyticContact((answer.partnerUser as Record<string, unknown>).olyticContact) };
}

/* ── A partner's referrals (MOTHER-123) ────────────────────────── */
//
//   GET /api/partners/users/:id/referrals?stage=&limit=&offset=   { referrals, total, counts, limit, offset }
//
// MOTHER-122's route: newest first; `total` counts what `stage` matches, `counts` every referral
// of the partner by stage. A 404 is 'No such partner.'; a bad stage or page is a 400 { error }.

/** At most this many referrals a page on the partner's page. */
export const REFERRALS_PAGE_SIZE = 25;

/** partner_referral.stage (migration 0069), in funnel order: the order of the section's tabs. */
export const REFERRAL_STAGES = ['submitted', 'contacted', 'in_discussions', 'closed_won', 'closed_lost', 'not_a_fit'] as const;
export type ReferralStage = (typeof REFERRAL_STAGES)[number];
export type ReferralCounts = Record<ReferralStage, number>;

/** One referral as staff see it (`staffReferralView`): no email, phone or internal note. */
export interface PartnerReferral {
  id: string;
  firstName: string;
  lastName: string;
  companyName: string;
  stage: string;
  stageChangedAt: string | null;
  createdAt: string;
  leadId: string | null;
  accountId: string | null;
  opportunityId: string | null;
}

export interface PartnerReferralList {
  referrals: PartnerReferral[];
  /** Referrals the stage matches (every one without a stage), across every page. */
  total: number;
  /** Every referral of the partner by stage, whatever the stage asked for: the tabs' counts. */
  counts: ReferralCounts;
  limit: number;
  offset: number;
}

export interface PartnerReferralQuery {
  /** A stage, or empty for all of them. */
  stage?: ReferralStage | '' | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

/** The referrals route's URL: `stage` only when given, `limit` and `offset` always. */
export function partnerReferralsUrl(id: string, { stage, limit = REFERRALS_PAGE_SIZE, offset = 0 }: PartnerReferralQuery = {}): string {
  const params = new URLSearchParams();
  if (stage) params.set('stage', stage);
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  return `${partnerUrl(id)}/referrals?${params.toString()}`;
}

function parseReferral(value: unknown): PartnerReferral {
  if (!isObject(value) || typeof value.id !== 'string') throw new PartnersApiError('The server sent a referral the page cannot read.', 500);
  return {
    id: value.id,
    firstName: str(value.firstName),
    lastName: str(value.lastName),
    companyName: str(value.companyName),
    stage: str(value.stage),
    stageChangedAt: strOrNull(value.stageChangedAt),
    createdAt: str(value.createdAt),
    leadId: strOrNull(value.leadId),
    accountId: strOrNull(value.accountId),
    opportunityId: strOrNull(value.opportunityId),
  };
}

/** A partner's referrals: a 404 throws "No such partner."; any other failure, the server's text when it sent one. */
export async function listPartnerReferrals(fetcher: Fetch, id: string, query: PartnerReferralQuery = {}): Promise<PartnerReferralList> {
  const response = await fetcher(partnerReferralsUrl(id, query));
  if (response.status === 404) {
    await response.body?.cancel().catch(() => undefined);
    throw new PartnersApiError(NO_SUCH_PARTNER, 404);
  }
  const body = await read(response, 'Could not load referrals.');
  if (!Array.isArray(body.referrals)) throw new PartnersApiError('The server sent referrals the page cannot read.', 500);
  const counts = isObject(body.counts) ? body.counts : {};
  return {
    referrals: body.referrals.map(parseReferral),
    total: count(body.total),
    counts: Object.fromEntries(REFERRAL_STAGES.map((s) => [s, count(counts[s])])) as ReferralCounts,
    limit: count(body.limit) || REFERRALS_PAGE_SIZE,
    offset: count(body.offset),
  };
}

/* ── A partner's referral rate (MOTHER-144) ────────────────────── */
//
//   PATCH /api/partners/users/:id { referralRate }            { partnerUser }   (MOTHER-143; with referralRate)
//   GET   /api/partners/users/:id/referral-rate-history       { changes: [{ oldRate, newRate, changedBy, changedAt }] }
//
// Admins only (Cameron's card on OLYAA-118): from 0 to 25 with at most 2 decimals; the change applies
// to referrals submitted after it, and the partner is not told. A bad rate is a 400 { error }.

/** The highest rate staff may set (partners.mjs REFERRAL_RATE_MAX). */
export const REFERRAL_RATE_MAX = 25;
/** What the page says, before sending anything, for a rate the route would refuse. */
export const REFERRAL_RATE_INVALID = `Enter a rate from 0 to ${REFERRAL_RATE_MAX}, with at most 2 decimals`;

// Digits with up to 2 decimals (`12`, `12.5`, `.25`): no sign, exponent or third decimal.
const RATE_TEXT = /^(?:\d+(?:\.\d{0,2})?|\.\d{1,2})$/;

/** The rate typed, as the number to send, or null when the route would refuse it (below 0, above 25, over 2 decimals). */
export function parseReferralRate(typed: string): number | null {
  const text = typed.trim();
  if (!RATE_TEXT.test(text)) return null;
  const rate = Number(text);
  return Number.isFinite(rate) && rate >= 0 && rate <= REFERRAL_RATE_MAX ? rate : null;
}

/** A rate as the page shows it: `10%`, `12.5%`, `12.25%`; '' for none. */
export function formatReferralRate(rate: number | null): string {
  return rate === null || !Number.isFinite(rate) ? '' : `${rate.toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;
}

const rateOrNull = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);

/** What the change answers: the partner as the list shows them, and their rate now. */
export interface ReferralRateWrite {
  partnerUser: PartnerUser;
  referralRate: number | null;
}

/** Sets the partner's referral rate: exactly `{ referralRate }`. A refusal throws the server's text. */
export async function setReferralRate(fetcher: Fetch, id: string, referralRate: number): Promise<ReferralRateWrite> {
  const init: RequestInit = { method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify({ referralRate }) };
  const answer = await read(await fetcher(partnerUrl(id), init), 'Could not change the referral rate.');
  const partnerUser = parsePartnerUser(answer.partnerUser);
  return { partnerUser, referralRate: rateOrNull((answer.partnerUser as Record<string, unknown>).referralRate) };
}

/** One change of a partner's rate. `changedBy` is the staff member's name, or null when the server knows nobody. */
export interface ReferralRateChange {
  oldRate: number | null;
  newRate: number | null;
  changedBy: string | null;
  changedAt: string;
}

export const referralRateHistoryUrl = (id: string): string => `${partnerUrl(id)}/referral-rate-history`;

/** Every change of the partner's rate, newest first, in the server's order. A failure throws the server's text when it sent one. */
export async function listReferralRateHistory(fetcher: Fetch, id: string): Promise<ReferralRateChange[]> {
  const body = await read(await fetcher(referralRateHistoryUrl(id)), 'Could not load the rate history.');
  if (!Array.isArray(body.changes)) throw new PartnersApiError('The server sent a rate history the page cannot read.', 500);
  return body.changes.filter(isObject).map((change) => ({
    oldRate: rateOrNull(change.oldRate),
    newRate: rateOrNull(change.newRate),
    changedBy: strOrNull(change.changedBy),
    changedAt: str(change.changedAt),
  }));
}
